  import { useState, useEffect } from 'react'
  import { useTranslation } from 'react-i18next'
  import { Card, CardContent } from '@/components/ui/card'
  import { PageHeader } from '@/components/app/PageHeader'
  import { Skeleton } from '@/components/ui/skeleton'
  import { Clock } from 'lucide-react'
  import { cn } from '@/lib/utils'
  import {
    type ChangelogData,
    changelogColorStyle,
    getChangelogIcon,
  } from '@/features/admin/lib/changelog'
  import { FallBeamBackground } from '@/components/backgrounds/FallBeamBackground'

  export function ChangelogPage() {
    const { t, i18n } = useTranslation()
    const [data, setData] = useState<ChangelogData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    useEffect(() => {
      const lang = i18n.language === 'fr' ? 'fr' : 'en'
      fetch(`/changelog-${lang}.json`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch changelog')
          return res.json()
        })
        .then((json: ChangelogData) => {
          setData(json)
          setLoading(false)
        })
        .catch(() => {
          setError(true)
          setLoading(false)
        })
    }, [i18n.language])

    const latestColor = data?.entries[0]?.color

    if (loading) {
      return (
        <div className="relative">
          <FallBeamBackground beamColorClass={latestColor ?? 'slate'} className="opacity-40" />
          <div className="relative mx-auto max-w-3xl space-y-5">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      )
    }

    if (error || !data) {
      return (
        <div className="relative">
          <FallBeamBackground beamColorClass={latestColor ?? 'slate'} className="opacity-40" />
          <div className="relative flex h-64 items-center justify-center rounded-xl border border-dashed mx-auto max-w-3xl">
            <p className="text-sm text-muted-foreground">{t('changelog.error')}</p>
          </div>
        </div>
      )
    }

    return (
      <div className="relative">
        <FallBeamBackground beamColorClass={latestColor ?? 'cyan-400'} />

        <div className="relative mx-auto max-w-3xl">
          <div className="space-y-1">
            <PageHeader title={t('changelog.title')} />
            <p className="text-sm text-muted-foreground">
              {t('changelog.description')}
            </p>
          </div>

          {/* Cards — sorted by date+time descending (most recent first) */}
          <div className="mt-5 flex flex-col gap-5">
          {[...data.entries]
            .sort((a, b) => {
              const timeA = new Date(`${a.date}T${a.time.replace(' UTC+1', '')}`).getTime()
              const timeB = new Date(`${b.date}T${b.time.replace(' UTC+1', '')}`).getTime()
              return timeB - timeA
            })
            .map((entry) => {
            const Icon = getChangelogIcon(entry.type)
            const style = changelogColorStyle(entry.color)
            const formattedDate = new Intl.DateTimeFormat(
              i18n.language === 'fr' ? 'fr-FR' : 'en-US',
              { day: 'numeric', month: 'long', year: 'numeric' },
            ).format(new Date(entry.date))

            return (
              <Card
                key={entry.id}
                className="rounded-lg border-0 bg-card shadow-none app-card-padding"
              >
                <CardContent className="p-0">
                  {/* Card Head */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Type Badge with Icon inside */}
                    <span
                      className={cn(
                        'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wider',
                        style,
                      )}
                    >
                      <Icon className="size-4" />
                      {entry.type}
                    </span>

                    {/* Timestamp */}
                    <span className="ml-auto flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                      <Clock className="size-3" />
                      {formattedDate}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="mt-3 text-base font-semibold tracking-tight text-foreground">
                    {entry.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {entry.description}
                  </p>

                  {/* Items List — neutral text */}
                  {entry.items.length > 0 && (
                    <ul className="mt-3 grid gap-1.5">
                      {entry.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="mt-2 size-1 shrink-0 rounded-full bg-muted-foreground/50" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Meta — production text left, version badge right */}
                  <div className="mt-3.5 flex items-center justify-between border-t pt-3">
                    <span className="text-xs text-muted-foreground">
                      {t('changelog.meta.production')} ·{' '}
                      <span className="capitalize">{entry.type}</span>
                    </span>
                    <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground">
                      v{entry.version}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
    )
  }
